import { Avatar, Button } from "antd";
import { type PublishPageResponse } from "@apis/http/api";
import { UserPublishResultResponseStatus } from "@apis/generated/openapi";
import { Trans, useTranslation } from "react-i18next";
import { formatKMB, normalizeNameWithAt } from "@utils/util";
import { useFormatDateTime } from "@hooks/useFormatDateTime";
import { IconLineClock } from "@kalodata/kds-design-icons";
import { twMerge } from "tailwind-merge";
import { DarkTooltip } from "@components/DarkTooltip";
import { type LinkedAccount } from "@store/linkedAccountSlice";
import { REGION_TIMEZONE_MAP, DEFAULT_TIMEZONE } from "@src/config/timezone";

type PublisherTab = "published" | "scheduled";
type PublishRecord = NonNullable<NonNullable<PublishPageResponse>["data"]>[number];

export interface PublishRecordCardProps {
  tab: PublisherTab;
  item: PublishRecord;
  accounts: LinkedAccount[];
  onUnschedule: (item: PublishRecord) => void;
  onRepublish: (item: PublishRecord) => void;
}

export const PublishRecordCard = (props: PublishRecordCardProps) => {
  const { tab, item, accounts, onRepublish, onUnschedule } = props;
  const { productImage, status, performance, accountName: accountNameValue } = item;
  const formatDateTime = useFormatDateTime();
  const isFailed = status === UserPublishResultResponseStatus.FAIL;
  const { t } = useTranslation();
  const soldCount = formatKMB(performance?.itemSoldCount, 0, t("sold"));
  const showScheduledAction = tab === "scheduled" && status === UserPublishResultResponseStatus.PENDING_PUBLISH;
  const showRepublishAction = tab === "scheduled" && isFailed;
  const accountName = normalizeNameWithAt(accountNameValue);
  const accountFallback = (item.accountName || "U").slice(0, 1).toUpperCase();

  const targetAccount = accounts.find((acc) => acc.id === item.linkId);
  const targetTimezone = targetAccount?.region
    ? REGION_TIMEZONE_MAP[targetAccount.region] || DEFAULT_TIMEZONE
    : DEFAULT_TIMEZONE;

  return (
    <div className="group flex flex-col gap-[12px] rounded-[8px] border border-kds-border bg-kds-bg-card p-[16px]">
      <div className="flex w-full items-center gap-[16px]">
        <div className="relative h-[160px] w-[90px] shrink-0 overflow-hidden rounded-[4px] bg-kds-bg-element">
          {item.assetCoverImageUrl ? (
            <img className="size-full object-cover" src={item.assetCoverImageUrl} alt="" />
          ) : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-between self-stretch">
          <div className="flex w-full flex-col gap-[8px]">
            <DarkTooltip title={item.productTitle || "--"}>
              <p className="truncate text-[20px] font-semibold leading-[24px] text-kds-fg">
                {item.productTitle || "--"}
              </p>
            </DarkTooltip>

            <div className="flex items-center gap-[24px]">
              <div className="flex items-center gap-[4px]">
                <Avatar src={item.accountImage || undefined} size={24}>
                  {accountFallback}
                </Avatar>
                <span className="text-[14px] font-normal leading-[20px] text-kds-fg">{accountName || "--"}</span>
              </div>

              <div className="flex items-center gap-[4px] text-[14px] font-normal leading-[20px]">
                <IconLineClock size={16} className="text-kds-fg-soft" />
                {tab === "published" ? (
                  <>
                    <span className="text-kds-fg-soft">
                      <Trans>Published:</Trans>
                    </span>
                    <span className="text-kds-fg">
                      {formatDateTime(item.postTime, "YYYY-MM-DD HH:mm", "--", targetTimezone)}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-kds-fg-soft">
                      <Trans>Est. Publish:</Trans>
                    </span>
                    <span className="text-kds-fg">
                      {formatDateTime(item.publishTime, "YYYY-MM-DD HH:mm", "--", targetTimezone)}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* 数据指标占位 slot：接口暂未提供眼睛/点赞/分享/评论数据，预留高度以保持垂直节奏 */}
            <div aria-hidden className="h-[21px] w-full" />
          </div>

          <div className="flex w-full items-center gap-[8px]">
            <div className="size-[32px] shrink-0 overflow-hidden rounded-[4px] border border-kds-border bg-kds-bg-element">
              {productImage ? <img className="size-full object-cover" src={productImage} alt="" /> : null}
            </div>
            <div className="flex min-w-0 flex-col">
              <DarkTooltip title={item.productTitle || "--"}>
                <p
                  className={twMerge(
                    "text-[12px] font-normal leading-[16px] text-kds-fg-soft",
                    soldCount ? "truncate" : "line-clamp-2",
                  )}
                >
                  {item.productTitle || "--"}
                </p>
              </DarkTooltip>
              {soldCount ? <p className="text-[14px] font-normal leading-[21px] text-kds-fg">{soldCount}</p> : null}
            </div>
          </div>
        </div>
      </div>

      <div className="h-px w-full bg-kds-border" />

      <div className="flex w-full items-center justify-between">
        {tab === "published" ? (
          <span className="inline-flex h-[28px] items-center justify-center rounded-full bg-kds-success-soft px-[10px] text-[14px] font-medium leading-[16px] text-kds-success-fg">
            <Trans>Published</Trans>
          </span>
        ) : isFailed ? (
          <span className="inline-flex h-[28px] items-center justify-center rounded-full bg-kds-danger-soft px-[10px] text-[14px] font-medium leading-[16px] text-kds-danger-fg">
            <Trans>Publish Failed</Trans>
          </span>
        ) : (
          <span className="inline-flex h-[28px] items-center justify-center rounded-full bg-kds-accent-soft px-[10px] text-[14px] font-medium leading-[16px] text-kds-accent-fg">
            <Trans>Scheduled</Trans>
          </span>
        )}

        {showScheduledAction ? (
          <Button
            className="h-[28px]! rounded-[6px] border-kds-border-solid bg-kds-bg-element px-[10px] text-[14px] font-medium leading-[16px] text-kds-fg opacity-0 shadow-[0_1px_3px_0_var(--kds-shadow)] transition-opacity group-hover:opacity-100"
            onClick={() => onUnschedule(item)}
          >
            <Trans>Unschedule</Trans>
          </Button>
        ) : null}

        {showRepublishAction ? (
          <Button
            className="h-[28px] rounded-[6px] border-kds-border-solid bg-kds-bg-element px-[10px] text-[14px] font-medium leading-[16px] text-kds-fg opacity-0 shadow-[0_1px_3px_0_var(--kds-shadow)] transition-opacity group-hover:opacity-100"
            onClick={() => onRepublish(item)}
          >
            <Trans>Republish</Trans>
          </Button>
        ) : null}
      </div>
    </div>
  );
};
